import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { ReactComponent as CartWithCards } from '../../media/images/cart-with-cards.svg';
import useMediaQueries from '../../hooks/useMediaQueries';

import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { BrandCardSlim } from 'components/brand-card/BrandCardSlim';
import { CardPropsEarns, convertBrandToCardProps } from 'components/bonus/CardProps';
import { useHistory } from 'react-router-dom';
import { Routes } from 'routes';
import { useDispatch } from 'react-redux';
import { applyTerm, updateSearch } from 'common/features/store/duck/search/duck';
import { constructorClient } from 'third-party-integrations/constructor';
import { constructorDataToBrandData } from 'common/utils/constructor';
import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';

interface Conf {
    [key: string]: { [key: string]: string };
}

interface IConfig {
    styles: Conf;
}

declare global {
    interface Window {
        clearSearchBar: (() => void) | undefined;
    }
}

const configs: IConfig = {
    styles: {
        mobile: createStyles({
            mainMessage: [
                'text-center text-dark-blue font-light',
                { fontSize: 24, lineHeight: '36px' },
            ],
            subMessage: ['mb-6 text-center text-grey-1', { fontSize: 16, lineHeight: '24px' }],
            cardContainer: { width: 150 },
        }),
        desktop: createStyles({
            mainMessage: [
                'text-center text-dark-blue font-light',
                { fontSize: 28, lineHeight: '42px' },
            ],
            subMessage: [
                'mb-9 text-center text-darkest mt-1 font-light',
                { fontSize: 20, lineHeight: '30px' },
            ],
            cardContainer: { width: 180 },
        }),
    },
};

const EmptyResults = ({
    term,
    isConstructorEnabled,
}: {
    term: string;
    isConstructorEnabled: boolean;
}) => {
    const [type, setType] = useState<'desktop' | 'mobile'>('desktop');
    const queries = useMemo(
        () => ({
            '(max-width: 640px)': () => {
                setType('mobile');
            },
            '(min-width: 641px)': () => {
                setType('desktop');
            },
        }),
        []
    );

    const [data, setData] = useState<CardPropsEarns[]>([]);

    useMediaQueries(queries);
    const baseFilters = useConstructorDefaultParameters({ numResults: 5, term });
    baseFilters.current.numResults = type === 'desktop' ? 5 : 4;

    // load suggested alternatives
    useEffect(() => {
        // NOTE: We _wait_ for initial render to happen in order for the
        // screen `type` detection to happen(defaults to desktop).
        // Otherwise, API call happens with `numResults = 5` instead of `numResults = 4`
        // when we're in mobile view
        if (isConstructorEnabled) {
            window.requestAnimationFrame(() => {
                constructorClient.recommendations
                    .getRecommendations('zero-results', baseFilters.current)
                    .then((results) => {
                        if (
                            results.response &&
                            results.response.results &&
                            results.response.total_num_results
                        ) {
                            const constructorBrands = results.response.results;
                            for (let i = 0; i < constructorBrands.length; i++) {
                                constructorBrands[i] = convertBrandToCardProps(
                                    constructorDataToBrandData(constructorBrands[i])
                                );
                            }
                            setData(constructorBrands as CardPropsEarns[]);
                        }
                    })
                    .catch((e) => {
                        console.error('Unable to fetch recommendations.');
                    });
            });
        }
    }, [term, isConstructorEnabled]);

    const styles = configs.styles[type];
    const history = useHistory();
    const dispatch = useDispatch<any>();

    const backToShopAllBrands = useCallback(() => {
        // NOTE: utilize the `clear` functionality that react-select provides
        // Exposed in `ConstructorSearchBar.tsx`
        if (window.clearSearchBar) {
            window.clearSearchBar();
        }
        dispatch(updateSearch({ term: '' }));
        dispatch(applyTerm(''));
        // Just navigate to /brands to make sure the user will see the brands page.
        history.push(Routes.Brands);
    }, [history, dispatch]);

    return (
        <div className="flex">
            <div className="h-full max-w-full mx-auto px-0 sm:px-4">
                <div className="px-6">
                    <div className="flex mb-4 sm:mb-8">
                        <div className="mx-auto mt-2">
                            <CartWithCards />
                        </div>
                    </div>
                    <p className={styles.mainMessage}>
                        Sorry, we couldn’t find a match for{' '}
                        <b className="text-brand-dark">"{term}"</b>
                    </p>
                    <p className={styles.subMessage}>
                        Try searching for another brand, or shop all brands.
                    </p>
                    <div className="flex flex-col justify-center items-center mb-11 sm:mb-20">
                        <Btn onClick={backToShopAllBrands}>Shop All Brands</Btn>
                    </div>
                </div>
                {data.length ? (
                    <div className="rounded-none sm:rounded-lg bg-grey-accent px-7 sm:px-20 py-8 sm:py-10 mb-0 sm:mb-16">
                        <p className="mb-8 text-brand-dark font-semibold text-xl sm:text-2xl">
                            Recommended for You
                        </p>

                        <div className="flex gap-4 flex-wrap">
                            {data.map((brand) => (
                                <BrandCardSlim key={brand.id} data={brand} styles={styles} />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default EmptyResults;
